import React, { useState, useEffect } from 'react';
import { Users, Calendar, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

export function Dashboard() {
  const [stats, setStats] = useState({
    activeCustomers: 0,
    upcomingTasks: 0,
    completedTasks: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        // Get active customers count
        const { data: companies, error: companiesError } = await supabase
          .from('companies')
          .select('id')
          .eq('user_id', user.id)
          .eq('status', 'active');

        if (companiesError) throw companiesError;

        // Get upcoming tasks count (pending tasks)
        const { data: upcomingTasks, error: upcomingError } = await supabase
          .from('tasks')
          .select('id')
          .eq('user_id', user.id)
          .eq('status', 'pending');

        if (upcomingError) throw upcomingError;

        // Get completed tasks count
        const { data: completedTasks, error: completedError } = await supabase
          .from('tasks')
          .select('id')
          .eq('user_id', user.id)
          .eq('status', 'completed');

        if (completedError) throw completedError;

        setStats({
          activeCustomers: companies?.length || 0,
          upcomingTasks: upcomingTasks?.length || 0,
          completedTasks: completedTasks?.length || 0,
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statItems = [
    {
      title: 'Aktive kunder',
      value: stats.activeCustomers,
      icon: Users,
      color: 'bg-blue-100 text-blue-800',
      iconColor: 'text-blue-500'
    },
    {
      title: 'Kommende opgaver',
      value: stats.upcomingTasks,
      icon: Calendar,
      color: 'bg-yellow-100 text-yellow-800',
      iconColor: 'text-yellow-500'
    },
    {
      title: 'Udførte opgaver',
      value: stats.completedTasks,
      icon: CheckCircle,
      color: 'bg-green-100 text-green-800',
      iconColor: 'text-green-500'
    }
  ];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Dashboard</h1>
      </div>
      
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-lg p-6 shadow-lg animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
              <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {statItems.map((stat) => {
            const Icon = stat.icon;
            return (
              <div key={stat.title} className="bg-white rounded-lg p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className={`p-3 rounded-full ${stat.color} bg-opacity-20`}>
                    <Icon className={stat.iconColor} size={24} />
                  </div>
                  <span className={`text-sm font-medium ${stat.color}`}>
                    Opdateret
                  </span>
                </div>
                <h2 className="text-gray-500 text-sm">{stat.title}</h2>
                <p className="text-3xl font-bold mt-2">{stat.value}</p>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}